import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  forwardRef,
  inject,
  input,
} from '@angular/core';
import {
  FormControl,
  ReactiveFormsModule,
  FormsModule,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { debounceTime, distinctUntilChanged, map, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IconComponent } from '../icon/icon.component';
import { IconName } from '@app/utils/enums/icon-enum';

@Component({
  selector: 'app-search-field',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchFieldComponent),
      multi: true,
    },
  ],
  imports: [
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    IconComponent,
  ],
  templateUrl: './search-field.component.html',
  styleUrl: './search-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchFieldComponent implements ControlValueAccessor, OnInit {
  public readonly placeholder = input('');

  private readonly destroyRef = inject(DestroyRef);

  protected readonly iconName = IconName;
  protected readonly searchControl = new FormControl('', { nonNullable: true });

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: string) => void = () => {};

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected onTouched: () => void = () => {};

  public writeValue(value: string | null): void {
    this.searchControl.setValue(value || '', { emitEvent: false });
  }

  public registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  ngOnInit(): void {
    this.setUpListener();
  }

  private setUpListener(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(500),
        map((value) => (value ?? '').trim()),
        distinctUntilChanged(),
        tap((res) => {
          this.onChange(res);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
